import {
  getZodEnumValues,
  getZodInnerSchema,
  getZodLiteralValue,
  getZodSchemaType,
  getZodStringFormat,
  isZodInteger,
  isZodNullable,
} from '@/components/ui/SchemaInput'
import type { FrontendDataType, FrontendFieldInfo, NodeDetailsData } from '@/lib/field-types'
import { convertValueToFieldInfo } from '@/lib/utils/node-execution-converter'
import type { WorkflowNode } from 'sprites-types/workflow/core'
import { z } from 'zod'

// Runtime-safe helpers that do not rely on instanceof (handles cross-package Zod instances)
// Note: keep only helpers that are actually used

function getObjectShape(schema: z.ZodType): Record<string, z.ZodType> | undefined {
  const def: any = (schema as any)?._def
  if (!def) return undefined
  const shape = typeof def.shape === 'function' ? def.shape() : def.shape
  return shape as Record<string, z.ZodType>
}

function getArrayElementSchema(schema: z.ZodType): z.ZodType | undefined {
  const def: any = (schema as any)?._def
  return def?.type as z.ZodType | undefined
}

// Convert Zod schema to frontend field info
function convertZodSchemaToFieldInfo(
  name: string,
  schema: z.ZodType,
  currentValue?: any
): FrontendFieldInfo {
  const isOptional = isZodNullable(schema)
  const effectiveSchema = getZodInnerSchema(schema)
  const schemaType = getZodSchemaType(effectiveSchema)

  const fieldInfo: FrontendFieldInfo = {
    name,
    value: currentValue,
    isOptional,
    isArray: false,
    type: convertZodTypeToFrontendType(schemaType, effectiveSchema),
  }

  // Handle literal values
  const literalValue = getZodLiteralValue(effectiveSchema)
  if (literalValue !== undefined) {
    fieldInfo.type = typeof literalValue as FrontendDataType
    fieldInfo.enumValues = [String(literalValue)]
    return fieldInfo
  }

  // Handle enum values
  const enumValues = getZodEnumValues(effectiveSchema)
  if (enumValues && Array.isArray(enumValues)) {
    fieldInfo.type = 'enum'
    fieldInfo.enumValues = enumValues.map(String)
    return fieldInfo
  }

  // Handle specific schema types
  switch (schemaType) {
    case 'string':
      const format = getZodStringFormat(effectiveSchema)
      if (format) {
        fieldInfo.format = format
        if (['date', 'date-time', 'time'].includes(format)) {
          fieldInfo.type = 'date'
        }
      }
      break

    case 'number':
      fieldInfo.type = isZodInteger(effectiveSchema) ? 'number' : 'number'
      break

    case 'array':
      fieldInfo.isArray = true
      const elementSchema = getArrayElementSchema(effectiveSchema) || effectiveSchema
      const elementType = getZodSchemaType(elementSchema)
      fieldInfo.arrayElementType = convertZodTypeToFrontendType(elementType, elementSchema)

      // Always create properties structure for arrays, even if empty
      if (Array.isArray(currentValue)) {
        fieldInfo.properties = currentValue.map((item, index) =>
          convertZodSchemaToFieldInfo(`[${index}]`, elementSchema, item)
        )
      } else {
        // Initialize empty array structure for proper recursive handling
        fieldInfo.properties = []
      }
      break

    case 'object':
      const shape = getObjectShape(effectiveSchema) || {}
      // Always create properties structure for objects, recursively
      fieldInfo.properties = Object.entries(shape).map(([propName, propSchema]) => {
        const propValue =
          currentValue && typeof currentValue === 'object' ? currentValue[propName] : undefined
        return convertZodSchemaToFieldInfo(propName, propSchema as z.ZodType, propValue)
      })
      break

    case 'discriminated-union':
      fieldInfo.type = 'discriminated-union'
      const discriminatedInfo = getDiscriminatedUnionInfo(effectiveSchema)
      fieldInfo.discriminatorKey = discriminatedInfo.discriminatorKey
      fieldInfo.discriminatorOptions = discriminatedInfo.discriminatorValues
      fieldInfo.discriminatorValue = currentValue?.[discriminatedInfo.discriminatorKey || '']

      // Find matching variant
      if (fieldInfo.discriminatorValue && discriminatedInfo.options) {
        const matchingOption = discriminatedInfo.options.find(
          (option: z.ZodObject<z.ZodRawShape>) => {
            const discriminatorField = option.shape?.[discriminatedInfo.discriminatorKey || '']
            if (discriminatorField) {
              const literalValue = getZodLiteralValue(discriminatorField)
              return literalValue === fieldInfo.discriminatorValue
            }
            return false
          }
        )

        if (matchingOption) {
          const variantFields = Object.entries(matchingOption.shape)
            .filter(([key]) => key !== discriminatedInfo.discriminatorKey)
            .map(([propName, propSchema]) =>
              convertZodSchemaToFieldInfo(
                propName,
                propSchema as z.ZodType,
                currentValue?.[propName]
              )
            )

          fieldInfo.unionVariant = { fields: variantFields }
        }
      }
      break

    case 'union':
      // For regular unions, try to infer from current value or show as unknown
      if (currentValue !== undefined) {
        fieldInfo.type = inferTypeFromValue(currentValue)
      } else {
        fieldInfo.type = 'unknown'
      }
      break
  }

  return fieldInfo
}

// Helper function for discriminated unions (similar to NodeDataDisplay)
function getDiscriminatedUnionInfo(schema: z.ZodType): {
  discriminatorKey?: string
  options?: z.ZodObject<z.ZodRawShape>[]
  discriminatorValues?: string[]
} {
  if (getZodSchemaType(schema) !== 'discriminated-union') {
    return {}
  }

  try {
    const def: any = (schema as any)?._def || {}
    const discriminatorKey: string | undefined = def.discriminator
    const options = (def.options || []) as z.ZodObject<z.ZodRawShape>[]

    const discriminatorValues: string[] = []
    options.forEach((option: z.ZodObject<z.ZodRawShape>) => {
      const discriminatorField = discriminatorKey ? option.shape?.[discriminatorKey] : undefined
      if (discriminatorField) {
        const literalValue = getZodLiteralValue(discriminatorField)
        if (literalValue !== undefined) {
          discriminatorValues.push(String(literalValue))
        }
      }
    })

    return { discriminatorKey, options, discriminatorValues }
  } catch (error) {
    console.warn('Failed to parse discriminated union:', error)
    return {}
  }
}

// Convert Zod schema type to frontend data type
function convertZodTypeToFrontendType(schemaType: string, schema: z.ZodType): FrontendDataType {
  switch (schemaType) {
    case 'string':
      return 'string'
    case 'number':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'array':
      return 'array'
    case 'object':
      return 'object'
    case 'enum':
      return 'enum'
    case 'discriminated-union':
      return 'discriminated-union'
    case 'union':
      // For unions, try to infer the most appropriate type
      if (schema && (schema as any)?._def?.options) {
        const options = (schema as any)._def.options
        // If all options are the same type, use that type
        const types = options.map((opt: z.ZodType) => getZodSchemaType(opt))
        const uniqueTypes = [...new Set(types)]
        if (uniqueTypes.length === 1) {
          return convertZodTypeToFrontendType(uniqueTypes[0] as string, options[0])
        }
      }
      return 'unknown'
    case 'optional':
    case 'nullable':
      // For optional/nullable, get the inner type
      const innerSchema = getZodInnerSchema(schema)
      const innerType = getZodSchemaType(innerSchema)
      return convertZodTypeToFrontendType(innerType, innerSchema)
    case 'date':
      return 'date'
    case 'null':
      return 'null'
    case 'undefined':
      return 'undefined'
    case 'literal':
      // For literals, infer type from the literal value
      const literalValue = getZodLiteralValue(schema)
      return inferTypeFromValue(literalValue)
    default:
      console.warn(`Unhandled Zod schema type: ${schemaType}`)
      return 'unknown'
  }
}

// Simple type inference from JavaScript values
function inferTypeFromValue(value: any): FrontendDataType {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (Array.isArray(value)) return 'array'

  const jsType = typeof value
  switch (jsType) {
    case 'string':
      return 'string'
    case 'number':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'object':
      return 'object'
    default:
      return 'unknown'
  }
}

// Convert WorkflowNode + Zod schema to NodeDetailsData
export function convertWorkflowNodeToFrontendData(
  node: WorkflowNode,
  inputSchema?: z.ZodType,
  isEditMode: boolean = false
): NodeDetailsData {
  const result: NodeDetailsData = { node, isEditMode }

  // Convert inputs
  if (inputSchema) {
    try {
      const schemaKind = getZodSchemaType(inputSchema)

      if (schemaKind === 'object') {
        const shape = getObjectShape(inputSchema) || {}
        const inputFields = Object.entries(shape).map(([inputId, zodSchema]) =>
          convertZodSchemaToFieldInfo(inputId, zodSchema as z.ZodType, node.inputs?.[inputId])
        )

        result.inputs = inputFields.filter((field) => field && field.name)
      } else if (schemaKind === 'discriminated-union') {
        // Represent the entire union as a single top-level field named "inputs"
        const unionField = convertZodSchemaToFieldInfo('inputs', inputSchema, node.inputs)
        result.inputs = [unionField]
      } else if (schemaKind === 'array') {
        // Handle array schemas
        const arrayField = convertZodSchemaToFieldInfo('inputs', inputSchema, node.inputs)
        result.inputs = [arrayField]
      } else if (schemaKind === 'union') {
        // Handle union schemas
        const unionField = convertZodSchemaToFieldInfo('inputs', inputSchema, node.inputs)
        result.inputs = [unionField]
      } else if (
        schemaKind === 'string' ||
        schemaKind === 'number' ||
        schemaKind === 'boolean' ||
        schemaKind === 'enum'
      ) {
        // Handle primitive types - if node.inputs is an object with multiple properties,
        // create individual fields for each property
        if (node.inputs && typeof node.inputs === 'object' && !Array.isArray(node.inputs)) {
          const inputFields = Object.entries(node.inputs).map(([inputId, value]) =>
            convertZodSchemaToFieldInfo(inputId, inputSchema, value)
          )
          result.inputs = inputFields.filter((field) => field && field.name)
        } else {
          // Single value case
          const field = convertZodSchemaToFieldInfo('value', inputSchema, node.inputs)
          result.inputs = [field]
        }
      } else {
        // For other schema types, try to create fields based on the actual input data
        if (node.inputs && typeof node.inputs === 'object' && !Array.isArray(node.inputs)) {
          const inputFields = Object.entries(node.inputs).map(([inputId, value]) =>
            convertZodSchemaToFieldInfo(inputId, inputSchema, value)
          )
          result.inputs = inputFields.filter((field) => field && field.name)
        } else {
          // Fallback to single field
          const field = convertZodSchemaToFieldInfo('value', inputSchema, node.inputs)
          result.inputs = [field]
        }
      }
    } catch (error) {
      console.error('Error converting Zod schema to frontend fields:', error)
      result.inputs = []
    }
  } else if (node.inputs && typeof node.inputs === 'object' && !Array.isArray(node.inputs)) {
    // No schema available - use value-based conversion
    const inputFields = Object.entries(node.inputs).map(([inputId, value]) =>
      convertValueToFieldInfo(inputId, value)
    )
    result.inputs = inputFields.filter((field) => field && field.name)
  } else if (node.inputs !== undefined && node.inputs !== null) {
    // Single value without schema
    const field = convertValueToFieldInfo('value', node.inputs)
    result.inputs = [field]
  }

  return result
}
